import { create } from "zustand";
import { CNSNotification } from "@/models/cns";
import {
  fetchNotifications,
  fetchUnseenCount,
  markAllNotificationsRead as markAllNotificationsReadService,
  markNotificationRead as markNotificationReadService,
  markNotificationSeen as markNotificationSeenService,
} from "@/services/notificationService";
import { getErrorMessage } from "@/types/problem-detail";

export type NotificationConnectionState = "idle" | "connecting" | "open" | "closed";

const INBOX_PAGE_SIZE = 50;

interface NotificationStore {
  notifications: CNSNotification[];
  unseenCount: number;
  unreadCount: number;
  loading: boolean;
  error: string | null;
  connectionState: NotificationConnectionState;
  /**
   * Pulls the latest inbox + unseen count. Called on initial mount and every
   * time the WebSocket transitions to {@code open} (i.e. every reconnect)
   * to backfill anything missed during the disconnect. Never on a polling
   * timer.
   */
  fetchInitial: () => Promise<void>;
  /**
   * Mark a single notification as seen. The server is the source of truth
   * for {@code seenAt}; this method optimistically updates the local row.
   */
  markSeen: (notificationId: string) => Promise<void>;
  /** Mark a notification as read (clicked). Stamps both seenAt and readAt. */
  markRead: (notificationId: string) => Promise<void>;
  /** Bulk: mark every unread notification as read. */
  markAllRead: () => Promise<void>;
  /** Mark any unseen items in the supplied list as seen. Called when the panel renders. */
  markSeenForVisible: (notificationIds: string[]) => Promise<void>;
  /** Apply a server-pushed notification (STOMP message) to the inbox. */
  applyServerPush: (notification: CNSNotification) => void;
  setConnectionState: (state: NotificationConnectionState) => void;
  /** Reset on logout / auth-context flip to null. */
  reset: () => void;
}

function recomputeCounts(notifications: CNSNotification[]) {
  let unseen = 0;
  let unread = 0;
  for (const n of notifications) {
    if (!n.seenAt) unseen += 1;
    if (!n.readAt) unread += 1;
  }
  return { unseenCount: unseen, unreadCount: unread };
}

export const useNotificationStore = create<NotificationStore>((set, get) => ({
  notifications: [],
  unseenCount: 0,
  unreadCount: 0,
  loading: false,
  error: null,
  connectionState: "idle",

  fetchInitial: async () => {
    set({ loading: true, error: null });
    try {
      const [page, count] = await Promise.all([
        fetchNotifications({ size: INBOX_PAGE_SIZE }),
        fetchUnseenCount(),
      ]);
      const notifications = page.content || [];
      const counts = recomputeCounts(notifications);
      set({
        notifications,
        unseenCount: count,
        unreadCount: counts.unreadCount,
        loading: false,
      });
    } catch (error) {
      set({ loading: false, error: getErrorMessage(error, "Failed to load notifications") });
    }
  },

  markSeen: async (notificationId) => {
    const before = get().notifications;
    const target = before.find((n) => n.id === notificationId);
    if (!target || target.seenAt) return;
    const optimistic = before.map((n) =>
      n.id === notificationId ? { ...n, seenAt: new Date().toISOString() } : n,
    );
    set({ notifications: optimistic, ...recomputeCounts(optimistic) });
    try {
      const updated = await markNotificationSeenService(notificationId);
      set((state) => {
        const replaced = state.notifications.map((n) =>
          n.id === updated.id ? { ...n, ...updated } : n,
        );
        return { notifications: replaced, ...recomputeCounts(replaced) };
      });
    } catch (error) {
      set({ notifications: before, ...recomputeCounts(before), error: getErrorMessage(error, "Failed to mark seen") });
    }
  },

  markRead: async (notificationId) => {
    const before = get().notifications;
    const optimistic = before.map((n) =>
      n.id === notificationId
        ? { ...n, readAt: new Date().toISOString(), seenAt: n.seenAt ?? new Date().toISOString() }
        : n,
    );
    set({ notifications: optimistic, ...recomputeCounts(optimistic) });
    try {
      const updated = await markNotificationReadService(notificationId);
      set((state) => {
        const replaced = state.notifications.map((n) =>
          n.id === updated.id ? { ...n, ...updated } : n,
        );
        return { notifications: replaced, ...recomputeCounts(replaced) };
      });
    } catch (error) {
      set({ notifications: before, ...recomputeCounts(before), error: getErrorMessage(error, "Failed to mark read") });
    }
  },

  markAllRead: async () => {
    const before = get().notifications;
    const now = new Date().toISOString();
    const optimistic = before.map((n) => ({
      ...n,
      seenAt: n.seenAt ?? now,
      readAt: n.readAt ?? now,
    }));
    set({ notifications: optimistic, unseenCount: 0, unreadCount: 0 });
    try {
      await markAllNotificationsReadService();
    } catch (error) {
      set({ notifications: before, ...recomputeCounts(before), error: getErrorMessage(error, "Failed to mark all read") });
    }
  },

  markSeenForVisible: async (notificationIds) => {
    const visibleUnseenIds = notificationIds.filter((id) => {
      const found = get().notifications.find((n) => n.id === id);
      return found && !found.seenAt;
    });
    if (visibleUnseenIds.length === 0) return;
    // N parallel single-item PATCHes per plan section 6d. HTTP/2 multiplexes; no bulk endpoint.
    await Promise.allSettled(visibleUnseenIds.map((id) => get().markSeen(id)));
  },

  applyServerPush: (notification) => {
    set((state) => {
      const exists = state.notifications.some((n) => n.id === notification.id);
      const next = exists
        ? state.notifications.map((n) => (n.id === notification.id ? notification : n))
        : [notification, ...state.notifications];
      return { notifications: next, ...recomputeCounts(next) };
    });
  },

  setConnectionState: (connectionState) => set({ connectionState }),

  reset: () =>
    set({
      notifications: [],
      unseenCount: 0,
      unreadCount: 0,
      loading: false,
      error: null,
      connectionState: "idle",
    }),
}));
